import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarBlank, Clock, MapPin, PencilSimple, UsersThree } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FormSkeleton } from '@/components/ui/form-skeleton';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import { ActivityFormModal } from '@/components/schedule/ActivityFormModal';
import { getActivity, updateActivity, type ActivityInput } from '@/api/activities';
import { listLocations } from '@/api/locations';
import {
  closeCalling,
  createCalling,
  listInvitationsForActivity,
  respondCalling,
  setCallingExclusions,
  type ActivityInvitation,
  type CallingResponseStatus,
} from '@/api/callings';

// ActivityDetail — full-page view of one activity: header (type, when, where),
// description, and the calling section with the per-member RSVP roster. Reached
// by clicking an activity in the schedule. Leader/admin surface: the calling
// roster endpoint is leader-of-the-activity / org-admin scoped, and a team
// leader / admin may record an RSVP on behalf of any roster member.
export function ActivityDetail() {
  const { t, i18n } = useTranslation('common');
  const { slug = '', id = '' } = useParams<{ slug: string; id: string }>();
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();

  const [editing, setEditing] = useState(false);

  const activityQuery = useQuery({
    queryKey: ['activity', slug, id],
    queryFn: () => getActivity(token, slug, id),
    enabled: !!slug && !!id,
  });
  const callingQuery = useQuery({
    queryKey: ['activity-calling', slug, id],
    queryFn: () => listInvitationsForActivity(token, slug, id),
    enabled: !!slug && !!id,
  });
  const { data: locationsRes } = useQuery({
    queryKey: ['locations', slug, ''],
    queryFn: () => listLocations(token, slug),
    enabled: !!slug,
  });

  const activity = activityQuery.data;
  const callingData = callingQuery.data;
  const calling = callingData?.calling ?? null;
  const invitations = callingData?.invitations ?? [];

  const locationName = useMemo(() => {
    if (!activity?.location_id) return '';
    const loc = (locationsRes?.locations ?? []).find((l) => l.id === activity.location_id);
    if (!loc) return '';
    if (activity.zone_id) {
      const zone = loc.zones.find((z) => z.id === activity.zone_id);
      if (zone) return `${loc.name} · ${zone.name}`;
    }
    return loc.name;
  }, [activity, locationsRes]);

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'full' }),
    [i18n.language],
  );
  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit', hour12: false }),
    [i18n.language],
  );
  const dateTimeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
    [i18n.language],
  );

  const updateMut = useMutation({
    mutationFn: (input: ActivityInput) => updateActivity(token, slug, id, input),
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['activity', slug, id] });
      toast.success(t('activityDetail.toast.updated'));
    },
  });

  const createCallingMut = useMutation({
    mutationFn: () =>
      // Opt-out model: no invite list — the whole roster is called on create.
      createCalling(token, slug, {
        activity_id: id,
        title: activity?.title ?? '',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity-calling', slug, id] });
      toast.success(t('callings.toast.created'));
    },
    onError: () => toast.error(t('callings.toast.createFailed')),
  });

  const respondMut = useMutation({
    mutationFn: (vars: { memberID: string; status: 'attending' | 'declined' | 'maybe' }) =>
      respondCalling(token, slug, calling!.id, { member_id: vars.memberID, status: vars.status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity-calling', slug, id] });
    },
    onError: () => toast.error(t('callings.toast.respondFailed')),
  });

  // Block / unblock a single member by replacing the full excluded set.
  const excludeMut = useMutation({
    mutationFn: (vars: { memberID: string; block: boolean }) => {
      const next = vars.block
        ? Array.from(new Set([...excludedIds, vars.memberID]))
        : excludedIds.filter((mid) => mid !== vars.memberID);
      return setCallingExclusions(token, slug, calling!.id, next);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity-calling', slug, id] });
    },
    onError: () => toast.error(t('callings.toast.excludeFailed')),
  });

  const closeCallingMut = useMutation({
    mutationFn: () => closeCalling(token, slug, calling!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['activity-calling', slug, id] });
      toast.success(t('callings.toast.closed'));
    },
    onError: () => toast.error(t('callings.toast.closeFailed')),
  });

  async function onCloseCalling() {
    if (
      await confirm({
        title: t('callings.close.confirmTitle'),
        body: t('callings.close.confirmBody'),
        confirmLabel: t('callings.close.cta'),
      })
    ) {
      closeCallingMut.mutate();
    }
  }

  if (activityQuery.isLoading) {
    return (
      <div className="mx-auto w-full max-w-3xl p-4 md:p-8">
        <FormSkeleton rows={4} action={false} />
      </div>
    );
  }
  if (activityQuery.isError || !activity) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4 md:p-8">
        <BackLink slug={slug} navigate={navigate} label={t('activityDetail.back')} />
        <p className="text-sm text-destructive">{t('activityDetail.notFound')}</p>
      </div>
    );
  }

  const start = new Date(activity.starts_at);
  const end = new Date(activity.ends_at);
  const counts = summarise(invitations);
  const callingOpen = calling?.status === 'open';
  const excludedIds = invitations.filter((i) => i.status === 'excluded').map((i) => i.member_id);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 md:p-8">
      <BackLink slug={slug} navigate={navigate} label={t('activityDetail.back')} />

      {/* Header */}
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">
            {t(`schedule.activity.type.option.${activity.activity_type}`)}
          </Badge>
          {activity.teams.map((team) => (
            <span
              key={team.id}
              className="inline-flex items-center gap-1.5 rounded-pill border border-border px-2.5 py-0.5 text-xs font-semibold"
            >
              <span
                aria-hidden
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: team.color || 'var(--muted-foreground)' }}
              />
              {team.name}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
            {activity.title}
          </h1>
          <Button variant="outline" onClick={() => setEditing(true)}>
            <PencilSimple weight="bold" className="mr-1.5 h-4 w-4" />
            {t('activityDetail.edit')}
          </Button>
        </div>
        <dl className="flex flex-col gap-1.5 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <CalendarBlank weight="bold" className="h-4 w-4 shrink-0" />
            <span>{dateFmt.format(start)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock weight="bold" className="h-4 w-4 shrink-0" />
            <span>
              {timeFmt.format(start)}–{timeFmt.format(end)}
            </span>
          </div>
          {locationName ? (
            <div className="flex items-center gap-2">
              <MapPin weight="bold" className="h-4 w-4 shrink-0" />
              <span>{locationName}</span>
            </div>
          ) : null}
        </dl>
      </header>

      {activity.description ? (
        <p className="whitespace-pre-wrap text-sm text-foreground/90">{activity.description}</p>
      ) : null}

      {/* Calling section */}
      <section className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/80"
          >
            <UsersThree className="h-5 w-5" weight="bold" />
          </span>
          <div className="flex flex-1 flex-col gap-1">
            <h2 className="font-heading text-lg font-bold">{t('callings.section.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('callings.section.body')}</p>
          </div>
        </div>

        {!calling ? (
          <div className="flex flex-col items-start gap-3">
            {callingData?.scheduled_send_at ? (
              <p className="text-sm text-muted-foreground">
                {t('callings.section.scheduledFor', {
                  date: dateTimeFmt.format(new Date(callingData.scheduled_send_at)),
                })}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">{t('callings.section.none')}</p>
            )}
            <Button
              onClick={() => createCallingMut.mutate()}
              disabled={createCallingMut.isPending}
            >
              {createCallingMut.isPending ? t('common.loading') : t('callings.section.create')}
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <Badge variant={callingOpen ? 'default' : 'secondary'}>
                {t(`callings.status.${calling.status}`)}
              </Badge>
              <span className="text-muted-foreground">
                {t('callings.counts.attending')}: <strong className="text-foreground">{counts.attending}</strong>
              </span>
              <span className="text-muted-foreground">
                {t('callings.counts.maybe')}: <strong className="text-foreground">{counts.maybe}</strong>
              </span>
              <span className="text-muted-foreground">
                {t('callings.counts.declined')}: <strong className="text-foreground">{counts.declined}</strong>
              </span>
              <span className="text-muted-foreground">
                {t('callings.counts.invited')}: <strong className="text-foreground">{counts.invited}</strong>
              </span>
              {callingOpen ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={onCloseCalling}
                  disabled={closeCallingMut.isPending}
                >
                  {t('callings.close.cta')}
                </Button>
              ) : null}
            </div>

            {calling.message ? (
              <p className="rounded-xl bg-muted/40 px-3 py-2 text-sm text-foreground/90">
                {calling.message}
              </p>
            ) : null}

            <ul className="flex flex-col divide-y divide-border">
              {invitations.map((inv) => {
                const blocked = inv.status === 'excluded';
                return (
                  <li
                    key={inv.member_id}
                    className={`flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between ${
                      blocked ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-sm font-semibold">{inv.name}</span>
                      <StatusChip status={inv.status} t={t} />
                    </div>
                    {callingOpen ? (
                      blocked ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="shrink-0"
                          onClick={() => excludeMut.mutate({ memberID: inv.member_id, block: false })}
                          disabled={excludeMut.isPending}
                        >
                          {t('callings.exclude.unblock')}
                        </Button>
                      ) : (
                        <div className="flex shrink-0 items-center gap-1.5">
                          <RsvpButton
                            active={inv.status === 'attending'}
                            label={t('callings.respond.yes')}
                            onClick={() => respondMut.mutate({ memberID: inv.member_id, status: 'attending' })}
                            disabled={respondMut.isPending}
                          />
                          <RsvpButton
                            active={inv.status === 'maybe'}
                            label={t('callings.respond.maybe')}
                            onClick={() => respondMut.mutate({ memberID: inv.member_id, status: 'maybe' })}
                            disabled={respondMut.isPending}
                          />
                          <RsvpButton
                            active={inv.status === 'declined'}
                            label={t('callings.respond.no')}
                            onClick={() => respondMut.mutate({ memberID: inv.member_id, status: 'declined' })}
                            disabled={respondMut.isPending}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="shrink-0 text-muted-foreground"
                            onClick={() => excludeMut.mutate({ memberID: inv.member_id, block: true })}
                            disabled={excludeMut.isPending}
                          >
                            {t('callings.exclude.block')}
                          </Button>
                        </div>
                      )
                    ) : null}
                  </li>
                );
              })}
              {invitations.length === 0 ? (
                <li className="py-3 text-sm text-muted-foreground">{t('callings.section.noInvitees')}</li>
              ) : null}
            </ul>
          </>
        )}
      </section>

      {editing ? (
        <ActivityFormModal
          slug={slug}
          initial={{
            id: activity.id,
            title: activity.title,
            description: activity.description ?? '',
            activity_type: activity.activity_type,
            location_id: activity.location_id ?? null,
            zone_id: activity.zone_id ?? null,
            starts_at: activity.starts_at,
            ends_at: activity.ends_at,
            team_ids: activity.team_ids,
            public_visibility: activity.public_visibility,
          }}
          submitting={updateMut.isPending}
          error={updateMut.isError ? t('activityDetail.toast.updateFailed') : null}
          onSubmit={(input) => updateMut.mutateAsync(input)}
          onCancel={() => setEditing(false)}
        />
      ) : null}
    </div>
  );
}

function BackLink({
  slug,
  navigate,
  label,
}: {
  slug: string;
  navigate: ReturnType<typeof useNavigate>;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={() => navigate(`/orgs/${slug}/schedule`)}
      className="flex w-fit items-center gap-1.5 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft weight="bold" className="h-4 w-4" />
      {label}
    </button>
  );
}

function RsvpButton({
  active,
  label,
  onClick,
  disabled,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? 'default' : 'outline'}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  );
}

function StatusChip({
  status,
  t,
}: {
  status: CallingResponseStatus;
  t: (key: string) => string;
}) {
  const variant =
    status === 'attending'
      ? 'default'
      : status === 'declined'
        ? 'destructive'
        : status === 'maybe'
          ? 'secondary'
          : 'outline';
  return <Badge variant={variant}>{t(`callings.status.${status}`)}</Badge>;
}

function summarise(invitations: ActivityInvitation[]) {
  const counts = { attending: 0, maybe: 0, declined: 0, invited: 0 };
  for (const inv of invitations) {
    if (inv.status in counts) counts[inv.status as keyof typeof counts] += 1;
  }
  return counts;
}
